import { useEffect, useState } from "react";

import { useTranslation } from "react-i18next";

import { Text } from "components";
import { DashboardService } from "services";
import { Bank, BankDashboardStatusResponse, DashboardAgentFilterParams, DashboardScope } from "types";

import classes from "./BankStatusSection.module.css";

interface BankStatusSectionProps {
  className?: string;
  scope?: DashboardScope;
  selectedAgentIds?: number[] | null;
}

type TrackedBank = Extract<Bank, "BANK_NOW" | "CEMBRA_MONEY_AG" | "CORNER">;

interface BankStatusMetric {
  bank: TrackedBank;
  bankName: string;
  realizedValue: number;
  percentageValue: number;
}

const TRACKED_BANKS: ReadonlyArray<{ bank: TrackedBank; bankName: string }> = [
  { bank: "BANK_NOW", bankName: "BankNOW" },
  { bank: "CEMBRA_MONEY_AG", bankName: "Cembra" },
  { bank: "CORNER", bankName: "Corner" },
];

const getCompletionPercent = (realizedValue: number, targetValue: number) => {
  if (targetValue <= 0) {
    return 0;
  }

  return (realizedValue / targetValue) * 100;
};

const formatRealizedValue = (value: number) => {
  if (value >= 1_000_000) {
    const valueInMillions = value / 1_000_000;
    const decimals = Number.isInteger(valueInMillions) ? 0 : 1;

    return `${valueInMillions.toFixed(decimals)} M`;
  }

  return new Intl.NumberFormat("de-DE", { maximumFractionDigits: 0 }).format(value);
};

const formatPercent = (value: number, locale: string) => {
  const hasFraction = !Number.isInteger(value);
  const formattedValue = new Intl.NumberFormat(locale, {
    minimumFractionDigits: hasFraction ? 1 : 0,
    maximumFractionDigits: 1,
  }).format(value);

  return `${formattedValue}%`;
};

const clampBarPercent = (value: number) => Math.min(Math.max(value, 0), 100);

const mapBankStatusToMetrics = (items: BankDashboardStatusResponse[]): BankStatusMetric[] =>
  TRACKED_BANKS.map(({ bank, bankName }) => {
    const item = items.find((entry) => entry.bank === bank);

    return {
      bank,
      bankName,
      realizedValue: item?.realized ?? 0,
      percentageValue:
        typeof item?.percentage === "number" && Number.isFinite(item.percentage)
          ? item.percentage
          : getCompletionPercent(item?.realized ?? 0, item?.target ?? 0),
    };
  });

const createEmptyBankStatusMetrics = (): BankStatusMetric[] => mapBankStatusToMetrics([]);

const BankStatusSection = ({
  className,
  scope = "admin",
  selectedAgentIds,
}: BankStatusSectionProps) => {
  const { t, i18n } = useTranslation();
  const [metrics, setMetrics] = useState<BankStatusMetric[]>(createEmptyBankStatusMetrics());
  const locale = i18n.language || "sr";

  useEffect(() => {
    let mounted = true;
    const params: DashboardAgentFilterParams | undefined = selectedAgentIds?.length
      ? { agentIds: selectedAgentIds }
      : undefined;

    const loadBankStatus = async () => {
      try {
        const data = await DashboardService.getBankStatus(scope, params);

        if (!mounted) {
          return;
        }

        setMetrics(mapBankStatusToMetrics(data ?? []));
      } catch {
        if (!mounted) {
          return;
        }

        setMetrics(createEmptyBankStatusMetrics());
      }
    };

    loadBankStatus();

    return () => {
      mounted = false;
    };
  }, [scope, selectedAgentIds]);

  return (
    <section className={[classes.root, className].filter(Boolean).join(" ")}>
      <div className={classes.header}>
        <Text className={classes.title}>{t("dashboard.admin.banks.title")}</Text>
      </div>

      <div className={classes.metrics}>
        {metrics.map((metric) => {
          const barPercent = clampBarPercent(metric.percentageValue);

          return (
            <div key={metric.bank} className={classes.row}>
              <Text className={classes.bankName}>{metric.bankName}</Text>

              <div className={classes.track}>
                <div
                  className={classes.fill}
                  style={{ width: `${barPercent}%` }}
                >
                  <Text className={classes.value}>{formatRealizedValue(metric.realizedValue)}</Text>
                </div>
              </div>

              <Text className={classes.percentage}>{formatPercent(metric.percentageValue, locale)}</Text>
            </div>
          );
        })}
      </div>
    </section>
  );
};

export default BankStatusSection;
